import useSWR from 'swr';

import { useSelf } from './useSelf';
import { useStytchClient } from './useStytchClient';

type RbacMap = {
  'stytch.organization': [
    'update.info.name',
    'update.info.slug',
    'update.info.logo-url',
    'update.settings.allowed-auth-methods',
    'update.settings.allowed-mfa-methods',
    'update.settings.email-jit-provisioning',
    'update.settings.email-invites',
    'update.settings.allowed-domains',
    'update.settings.default-sso-connection',
    'update.settings.sso-jit-provisioning',
    'update.settings.mfa-policy',
    'update.settings.implicit-roles',
    'update.settings.oauth-tenant-jit-provisioning',
    'update.settings.allowed-oauth-tenants',
    'delete',
  ];
  'stytch.member': [
    'create',
    'update.info.name',
    'update.info.email',
    'update.info.untrusted-metadata',
    'update.info.mfa-phone',
    'update.info.delete.mfa-phone',
    'update.info.delete.mfa-totp',
    'update.info.delete.password',
    'update.settings.is-breakglass',
    'update.settings.mfa-enrolled',
    'update.settings.default-mfa-method',
    'update.settings.roles',
    'search',
    'delete',
    'revoke-sessions',
  ];
  'stytch.sso': ['create', 'get', 'update', 'delete'];
  'stytch.self': [
    'update.info.name',
    'update.info.untrusted-metadata',
    'update.info.mfa-phone',
    'update.info.delete.mfa-phone',
    'update.info.delete.password',
    'update.info.delete.mfa-totp',
    'update.settings.mfa-enrolled',
    'update.settings.default-mfa-method',
    'delete',
    'revoke-sessions',
  ];
  'stytch.scim': ['create', 'get', 'update', 'delete'];
  'stytch.custom-org-roles': ['create', 'get', 'update', 'delete'];
};

type RbacResourceId = keyof RbacMap;

type RbacAction<TResourceId extends RbacResourceId> = RbacMap[TResourceId][number];

export const useRbac = <TResourceId extends RbacResourceId>(
  resourceId: TResourceId,
  action: RbacAction<TResourceId>,
) => {
  const client = useStytchClient();
  const { self } = useSelf();
  const memberId = self?.member_id;

  return useSWR(
    () => memberId && ['rbac', memberId, resourceId, action],
    () => client.rbac.isAuthorized(resourceId, action),
    {
      keepPreviousData: true,
    },
  );
};
